import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance, { checkTokenValidity, getUserDetails, getStaffId,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import { fetchPondsForCurrentUser } from "../../utils/pondListForUser";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import PondDetailStandardWeightUnits from "../../components/custom/PondDetailStandardWeightUnits";
import FishTypesSelection from "../../components/custom/FishTypesSelection";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";
import SamplingEffortFields, {
  buildDefaultAnglerSlots,
} from "../../components/custom/SamplingEffortFields";
import ClockTimeInput from "../../components/custom/ClockTimeInput";
import {
  requiresEffortUnits,
  isAnglingSamplingMethod,
  SAMPLING_METHOD_OPTIONS,
  isValidClockTimeInput,
  toClockTimeValue,
  normalizeSamplingMethod,
} from "../../utils/samplingCpueUtils";
import { displayMethodLabel } from "../../utils/samplingDisplayLabels";

/**
 * Optional UpdatePD UI blocks — hidden by default (same idea as create pond). State, handlers, and submit payload are unchanged.
 * Set any flag to `true` to show that block again. Entire panels are omitted when off (no empty cards).
 */
const SHOW_UPDATE_PD_WEATHER_PANEL = false;
const SHOW_UPDATE_PD_WATER_QUALITY_PANEL = true;
const SHOW_UPDATE_PD_FERTILIZATION_DATE_TYPE = false;

const showWeatherPanel = SHOW_UPDATE_PD_WEATHER_PANEL;
const showWaterQualityPanel = SHOW_UPDATE_PD_WATER_QUALITY_PANEL;
const showFertilizationDateType = SHOW_UPDATE_PD_FERTILIZATION_DATE_TYPE;

const WQ_EXTRA_FIELD_DEFS = [
  { id: 'BOD5', label: 'BOD5, mg/L' },
  { id: 'COD', label: 'COD, mg/L' },
  { id: 'Alkalinity', label: 'Alkalinity, mg/L CaCO3' },
  { id: 'NitriteN', label: 'Nitrite-N, mg/L NO2-N' },
  { id: 'TotalHardness', label: 'Total Hardness, mg/L CaCO3' },
  { id: 'PhosphateIon', label: 'Phosphate Ion, mg/L PO4' },
  { id: 'Phosphorous', label: 'Phosphorus, mg/L P' },
  { id: 'TotalAmmoniaNitrogen', label: 'Total Ammonia Nitrogen, mg/L NH3-N' },
];

const optionalWQFloat = (v) => {
  if (v === '' || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const wqNumToInputString = (v) => (v != null && v !== '' ? String(v) : '');

const UpdatePD = () => {
  const history = useHistory();
  const { id } = useParams(); // PondDetail ID
  const sessionFisherID = getUserDetails()?.FisherID;
  const [detailFisherID, setDetailFisherID] = useState(null);
  const fisherID = sessionFisherID ?? detailFisherID;
  const [ponds, setPonds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [pondFishTypes, setPondFishTypes] = useState([]);
  const [samplingFishTypes, setSamplingFishTypes] = useState([]);
  const [initialSamplingFishTypes, setInitialSamplingFishTypes] = useState([]); // Store initial fish types
  const [selectedWeightUnit, setSelectedWeightUnit] = useState("");
  const [selectedLengthUnit, setSelectedLengthUnit] = useState("");

  // Helper functions for date formatting (US Month-Day-Year for session file names)
  const formatDate = (dateObj) => {
    if (!dateObj || !(dateObj instanceof Date) || isNaN(dateObj)) return "";
    const day = dateObj.getDate().toString().padStart(2, "0");
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
    const year = dateObj.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formatLocalDateTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Initial form state
  const initialFormState = {
    PondDetailID: id,
    PondID: "0",
    Name: "",
    Date: null,
    TimeFishingStarted: "",
    TimeFishingStopped: "",
    SamplingMethod: "",
    AirTemperature: "",
    SkyClimate: "",
    WindSpeed: "",
    WindDirection: "",
    MoonPhase: "",
    BaroMetricPressure: "",
    WaterTemperature: "",
    WaterClarity: "",
    PH: "",
    DO: "",
    BOD5: "",
    COD: "",
    Alkalinity: "",
    NitriteN: "",
    TotalHardness: "",
    PhosphateIon: "",
    Phosphorous: "",
    TotalAmmoniaNitrogen: "",
    FertilizationDate: null,
    FertilizationType: "",
    NoFishes: 0,
    NoFishTypes: 0,
    FisherID: "",
    Weight_Unit: "",
    Length_Unit: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [effortCount, setEffortCount] = useState("");
  const [effortUnits, setEffortUnits] = useState([]);
  const [anglerCount, setAnglerCount] = useState("");
  const [fisherStaffOptions, setFisherStaffOptions] = useState([]);
  const [sessionAnglerSlots, setSessionAnglerSlots] = useState([]);
  const [fishSamples, setFishSamples] = useState([]);
  const [pondAnglers, setPondAnglers] = useState([]);

  const fetchFishSamples = useCallback(async () => {
    if (!id) return;
    try {
      const res = await axiosInstance.get("/api/FishSampling/SamplingData", {
        params: { PondDID: id },
      });
      setFishSamples(Array.isArray(res.data) ? res.data : []);
    } catch {
      setFishSamples([]);
    }
  }, [id]);

  const fetchSessionAnglers = useCallback(async (pondDetailId) => {
    if (!pondDetailId) {
      setPondAnglers([]);
      setSessionAnglerSlots([]);
      return;
    }
    try {
      const res = await axiosInstance.get("/api/PondDetail/PondAnglers", {
        params: { PondDID: pondDetailId },
      });
      const anglers = res.data?.anglers || [];
      setPondAnglers(anglers);
      const slots = anglers.map((a, i) => ({
        id: a.id ?? null,
        staffId: a.staffId ?? null,
        label: a.label || a.fullName || `Angler ${i + 1}`,
        fullName: a.fullName || null,
        sortOrder: a.sortOrder ?? i,
      }));
      setSessionAnglerSlots(slots);
      if (slots.length > 0) {
        setAnglerCount(String(slots.length));
      }
    } catch {
      setPondAnglers([]);
      setSessionAnglerSlots([]);
    }
  }, []);

  const fetchFisherTeamStaff = useCallback(async (scopedFisherID) => {
    const fid = scopedFisherID;
    if (!fid) {
      setFisherStaffOptions([]);
      return;
    }
    try {
      const res = await axiosInstance.get("/api/PondDetail/FisherTeamStaff", {
        params: { FisherID: fid },
      });
      const options = (res.data?.staff || []).map((s) => ({
        value: s.id,
        label: s.fullName,
      }));
      setFisherStaffOptions(options);
    } catch {
      setFisherStaffOptions([]);
    }
  }, []);

  const handleAnglerCountChange = (count) => {
    setAnglerCount(count);
    setFormErrors((prev) => ({ ...prev, SessionAnglers: "" }));
  };

  const handleSessionAnglerSlotsChange = (slots) => {
    const next = slots || [];
    setSessionAnglerSlots(next);
    setPondAnglers(
      next.map((slot, i) => ({
        id: slot.id ?? null,
        staffId: slot.staffId ?? null,
        label: slot.label || `Angler ${i + 1}`,
        fullName: slot.fullName || slot.label || `Angler ${i + 1}`,
        sortOrder: slot.sortOrder ?? i,
      }))
    );
    setFormErrors((prev) => ({ ...prev, SessionAnglers: "" }));
  };

  const saveSessionAnglers = async (pondDetailId, slots) => {
    if (!slots?.length) return;
    const res = await axiosInstance.put(`/api/SamplingSessionAnglers/${pondDetailId}`, {
      anglers: slots.map((slot, i) => ({
        id: slot.id || undefined,
        staffId: slot.staffId || null,
        label: slot.label || `Angler ${i + 1}`,
        sortOrder: slot.sortOrder ?? i,
      })),
    });
    const saved = res.data?.anglers || [];
    setPondAnglers(saved);
    setSessionAnglerSlots(
      saved.map((a, i) => ({
        id: a.id ?? null,
        staffId: a.staffId ?? null,
        label: a.label || a.fullName || `Angler ${i + 1}`,
        fullName: a.fullName || null,
        sortOrder: a.sortOrder ?? i,
      }))
    );
  };

  const saveEffortUnits = async (pondDetailId, samplingMethod, units) => {
    if (!requiresEffortUnits(samplingMethod) || !units?.length) return;
    await axiosInstance.put(`/api/SamplingEffort/${pondDetailId}`, {
      samplingMethod,
      units,
    });
  };

  // Fetch fish types for sampling data (selected fish types for PondDetail)
  const FetchFishTypesOfSamplingData = useCallback(async (opts = {}) => {
    const skipLoading = opts.skipLoading === true;
    try {
      if (!skipLoading) setIsLoading(true);
      const response = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`);
      const fishTypes = response.data || [];

      // Map to react-select format
      const formattedFishTypes = fishTypes.map((fish) => ({
        value: fish.FishTypeID,
        label: fish.FishTypeName,
        pondFishesID: fish.PondFishesID,
      }));

      setSamplingFishTypes(formattedFishTypes);
      setInitialSamplingFishTypes(formattedFishTypes); // Save initial fish types
      setFormData((prev) => ({
        ...prev,
        NoFishTypes: formattedFishTypes.length,
      }));
    } catch (error) {
      handleApiError(error, "Failed to fetch sampling fish types");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, [id]);

  // Fetch fish types for the selected pond (all available options)
  const fetchFishTypesOfPonds = useCallback(async (pondId, opts = {}) => {
    if (!pondId || pondId === "0") return;
    const skipLoading = opts.skipLoading === true;

    try {
      if (!skipLoading) setIsLoading(true);
      const response = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondId}`, {
        params: { search: "" },
      });

      setPondFishTypes(response.data || []);
    } catch (error) {
      handleApiError(error, "Failed to fetch pond fish types");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, []);

  // Fetch ponds data
  const fetchPonds = useCallback(async (opts = {}) => {
    const skipLoading = opts.skipLoading === true;
    try {
      if (!skipLoading) setIsLoading(true);
      const fetchedPonds = await fetchPondsForCurrentUser(getUserDetails());
      setPonds(fetchedPonds);
    } catch (error) {
      handleApiError(error, "Failed to fetch ponds");
    } finally {
      if (!skipLoading) setIsLoading(false);
    }
  }, []);

  // Fetch pond detail data
  const FetchPondDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      const detailParams = { id };
      if (sessionFisherID != null && sessionFisherID !== "") {
        detailParams.FisherID = sessionFisherID;
      }
      const response = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
        params: detailParams,
      });
      const data = response.data;
      const resolvedFisherID = data.FisherID ?? sessionFisherID;
      if (resolvedFisherID != null) {
        setDetailFisherID(resolvedFisherID);
      }
      console.log("pond Detail---", data);
      const formattedData = {
        ...data,
        PondDetailID: id,
        PondID: data.PondID?.toString() || "0",
        FisherID: resolvedFisherID ?? "",
        SamplingMethod: normalizeSamplingMethod(data.SamplingMethod) || data.SamplingMethod || "",
        Date: data.Date ? new Date(data.Date) : null,
        FertilizationDate: data.FertilizationDate ? new Date(data.FertilizationDate) : null,
        AirTemperature: data.AirTemperature?.toString() || "",
        WindSpeed: data.WindSpeed?.toString() || "",
        BaroMetricPressure: data.BaroMetricPressure?.toString() || "",
        WaterTemperature: data.WaterTemperature?.toString() || "",
        WaterClarity: wqNumToInputString(data.WaterClarity),
        PH: data.PH?.toString() || "",
        DO: data.DO != null && data.DO !== '' ? String(data.DO) : '',
        BOD5: wqNumToInputString(data.BOD5),
        COD: wqNumToInputString(data.COD),
        Alkalinity: wqNumToInputString(data.Alkalinity),
        NitriteN: wqNumToInputString(data.NitriteN),
        TotalHardness: wqNumToInputString(data.TotalHardness),
        PhosphateIon: wqNumToInputString(data.PhosphateIon),
        Phosphorous: wqNumToInputString(data.Phosphorous),
        TotalAmmoniaNitrogen: wqNumToInputString(data.TotalAmmoniaNitrogen),
        TimeFishingStarted: data.TimeFishingStarted
          ? toClockTimeValue(data.TimeFishingStarted)
          : "",
        TimeFishingStopped: data.TimeFishingStopped
          ? toClockTimeValue(data.TimeFishingStopped)
          : "",
        NoFishes: data.NoFishes || 0,
        NoFishTypes: data.NoFishTypes || 0,
        Weight_Unit: data.Weight_Unit?.toString() || "",
        Length_Unit: data.Length_Unit?.toString() || "",
      };

      // Normalize session file name to US MM-DD-YYYY (older saves used DD-MM-YYYY).
      if (formattedData.Date instanceof Date && !isNaN(formattedData.Date)) {
        const datePart = formatDate(formattedData.Date);
        const storedName = String(data.Name || "");
        const pondPrefix =
          data.PondName ||
          (storedName.replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "") !== storedName
            ? storedName.replace(/-\d{1,2}-\d{1,2}-\d{4}$/, "")
            : "");
        formattedData.Name = pondPrefix
          ? `${pondPrefix}-${datePart}`
          : datePart;
      }

      const units = data.EffortUnits || [];
      setEffortUnits(units);
      setEffortCount(units.length ? String(units.length) : "");
      const sessionAnglers = data.SessionAnglers || [];
      const slots = sessionAnglers.map((a, i) => ({
        id: a.id ?? null,
        staffId: a.staffId ?? null,
        label: a.label || a.fullName || `Angler ${i + 1}`,
        fullName: a.fullName || null,
        sortOrder: a.sortOrder ?? i,
      }));
      setSessionAnglerSlots(slots);
      setPondAnglers(sessionAnglers);
      setAnglerCount(
        slots.length > 0
          ? String(slots.length)
          : data.NumberOfAnglers != null && Number(data.NumberOfAnglers) > 0
            ? String(Number(data.NumberOfAnglers))
            : ""
      );
      if (
        slots.length === 0 &&
        data.NumberOfAnglers != null &&
        Number(data.NumberOfAnglers) > 0
      ) {
        setSessionAnglerSlots(buildDefaultAnglerSlots(data.NumberOfAnglers));
      }

      setFormData(formattedData);
      setSelectedWeightUnit(formattedData.Weight_Unit);
      setSelectedLengthUnit(formattedData.Length_Unit);

      // Nested helpers must not toggle isLoading — that hid then re-showed the full-page
      // preloader mid-init (looked like the page loaded twice).
      if (formattedData.PondID && formattedData.PondID !== "0") {
        await fetchFishTypesOfPonds(formattedData.PondID, { skipLoading: true });
      }
      await fetchFishSamples();
      if (isAnglingSamplingMethod(formattedData.SamplingMethod)) {
        await fetchFisherTeamStaff(resolvedFisherID);
        if (!sessionAnglers.length) {
          await fetchSessionAnglers(id);
        }
      } else {
        setPondAnglers([]);
        setSessionAnglerSlots([]);
      }
      await fetchPonds({ skipLoading: true });

    } catch (error) {
      handleApiError(error, "Failed to fetch pond detail");
    } finally {
      setIsLoading(false);
    }
  }, [sessionFisherID, id, fetchFishTypesOfPonds, fetchPonds, fetchFishSamples, fetchFisherTeamStaff, fetchSessionAnglers]);

  // Handle API errors
  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error("API Error:", error);
    }
  };

  // Handle pond change
  const handlePondChange = (e) => {
    const { value } = e.target;
    const selectedPond = ponds.find((item) => item.id === Number(value));

    setFormErrors((prev) => ({ ...prev, PondID: "" }));
    setFormData((prev) => ({
      ...prev,
      PondID: value,
      Name: selectedPond ? `${selectedPond.Name}-${formatDate(prev.Date || new Date())}` : formatDate(prev.Date || new Date()),
    }));

    // Fetch fish types for the new pond
    fetchFishTypesOfPonds(value);
    // Reset selected fish types when pond changes
    setSamplingFishTypes([]);
    setFormData((prev) => ({ ...prev, NoFishTypes: 0 }));
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormErrors((prev) => ({ ...prev, [id]: "" }));
    if (id === "SamplingMethod") {
      setEffortCount("");
      setEffortUnits([]);
      if (!isAnglingSamplingMethod(value)) {
        setAnglerCount("");
        setSessionAnglerSlots([]);
        setPondAnglers([]);
        setFisherStaffOptions([]);
      } else {
        // Initial load already fetches staff inside FetchPondDetail; only reload on user change.
        fetchFisherTeamStaff(fisherID ?? formData.FisherID);
      }
    }
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  // Handle date changes — ignore empty/invalid picker events (they used to snap to now).
  const handleDateChange = (e, type) => {
    const newDateTime = e.target.value;
    if (!newDateTime) return;
    const newDate = new Date(newDateTime);
    if (Number.isNaN(newDate.getTime())) return;

    if (type === 1) {
      const formattedDate = formatDate(newDate);
      const selectedPond = ponds.find((item) => item.id === Number(formData.PondID));
      setFormData((prev) => ({
        ...prev,
        Date: newDate,
        Name: selectedPond ? `${selectedPond.Name}-${formattedDate}` : formattedDate,
      }));
    } else if (type === 2) {
      setFormData((prev) => ({
        ...prev,
        FertilizationDate: newDate,
      }));
    }
  };

  const handleStandardWeightUnitsChange = ({ weightUnitId, lengthUnitId }) => {
    setSelectedWeightUnit(weightUnitId || "");
    setSelectedLengthUnit(lengthUnitId || "");
    setFormData((prev) => ({
      ...prev,
      Weight_Unit: weightUnitId || "",
      Length_Unit: lengthUnitId || "",
    }));
    setFormErrors((prev) => ({
      ...prev,
      Weight_Unit: "",
      Length_Unit: "",
      StandardWeightSystem: "",
    }));
  };

  // Handle fish types change
  const handleFishTypesChange = (selectedOptions) => {
    setSamplingFishTypes(selectedOptions || []);
    setFormErrors((prev) => ({ ...prev, FishTypes: "" }));
    setFormData((prev) => ({
      ...prev,
      NoFishTypes: selectedOptions?.length || 0,
    }));
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    let isValid = true;

    const requiredFields = [
      { field: "PondID", message: "Please select a pond", check: (val) => val && val !== "0" },
      { field: "Name", message: "Name is required", check: (val) => val?.trim() },
      {
        field: "TimeFishingStarted",
        message: "Timer start is required (h:mm AM/PM)",
        check: (val) => val?.trim() && isValidClockTimeInput(val),
      },
      {
        field: "TimeFishingStopped",
        message: "Timer stop is required (h:mm AM/PM)",
        check: (val) => val?.trim() && isValidClockTimeInput(val),
      },
      { field: "SamplingMethod", message: "Sampling method is required", check: (val) => val?.trim() },
      {
        field: "StandardWeightSystem",
        message: "Standard weight system is required",
        check: () => formData.Weight_Unit && formData.Length_Unit,
      },
      { field: "FishTypes", message: "At least one fish type is required", check: () => samplingFishTypes.length > 0 },
    ];

    requiredFields.forEach(({ field, message, check }) => {
      if (!check(formData[field])) {
        errors[field] = message;
        isValid = false;
      }
    });

    if (isAnglingSamplingMethod(formData.SamplingMethod)) {
      const n = parseInt(anglerCount, 10);
      if (!Number.isFinite(n) || n < 1 || sessionAnglerSlots.length < 1) {
        errors.SessionAnglers = "Enter the number of anglers for this fishing session";
        isValid = false;
      } else if (sessionAnglerSlots.some((slot) => !(slot.label || "").trim())) {
        errors.SessionAnglers = "Each angler slot needs a label";
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  const performUpdate = async (exitAfterSave) => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Update PondDetail
      const payload = {
        ...formData,
        FisherID: fisherID ?? formData.FisherID,
        Date: formData.Date?.toISOString() || new Date().toISOString(),
        FertilizationDate: formData.FertilizationDate?.toISOString() || null,
        NumberOfAnglers: isAnglingSamplingMethod(formData.SamplingMethod)
          ? parseInt(anglerCount, 10)
          : null,
        BOD5: optionalWQFloat(formData.BOD5),
        COD: optionalWQFloat(formData.COD),
        Alkalinity: optionalWQFloat(formData.Alkalinity),
        NitriteN: optionalWQFloat(formData.NitriteN),
        TotalHardness: optionalWQFloat(formData.TotalHardness),
        PhosphateIon: optionalWQFloat(formData.PhosphateIon),
        Phosphorous: optionalWQFloat(formData.Phosphorous),
        TotalAmmoniaNitrogen: optionalWQFloat(formData.TotalAmmoniaNitrogen),
      };

      await axiosInstance.put(`/api/PondDetail/UpdatePondDetail/${id}`, payload);
      await saveEffortUnits(id, formData.SamplingMethod, effortUnits);
      if (isAnglingSamplingMethod(formData.SamplingMethod)) {
        await saveSessionAnglers(id, sessionAnglerSlots);
      }

      // Step 2: Update SamplingFishes
      const currentFishTypeIds = samplingFishTypes.map((fish) => fish.value);
      const initialFishTypeIds = initialSamplingFishTypes.map((fish) => fish.value);

      // Fish types to add (in current but not in initial)
      const fishTypesToAdd = currentFishTypeIds.filter((fid) => !initialFishTypeIds.includes(fid));

      // Fish types to delete (in initial but not in current)
      const fishTypesToDelete = initialSamplingFishTypes.filter(
        (fish) => !currentFishTypeIds.includes(fish.value)
      );

      // Add new fish types
      for (const fishTypeId of fishTypesToAdd) {
        await axiosInstance.post(`/api/SamplingFishes/AddFish`, {
          PondDID: id,
          FishTypeID: fishTypeId,
          FisherID: fisherID ?? formData.FisherID,
          NumberOfFishes: 0,
          AverageLength: 0,
          StandardDeviationLength: 0,
          AverageWeight: 0,
          StandardDeviationWeight: 0,
          AverageWr: 0,
          StandardDeviationWr: 0,
          NumberOfFishesReleased: 0,
          NumberOfFishesCulled: 0,
        });
      }

      // Delete removed fish types
      for (const fish of fishTypesToDelete) {
        if (fish.pondFishesID) {
          await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${fish.pondFishesID}`);
        }
      }

      showSuccessToast(
        exitAfterSave
          ? "Pond detail updated — returning to Fishing Data"
          : "Pond detail and fish types updated successfully"
      );

      if (exitAfterSave) {
        history.push("/fishingData");
      } else {
        await FetchFishTypesOfSamplingData({ skipLoading: true });
      }
    } catch (error) {
      handleApiError(error, "Failed to update pond detail or fish types");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const sub = e.nativeEvent.submitter;
    const action = sub?.getAttribute?.("value") ?? sub?.value ?? "stay";
    const exitAfterSave = action === "exit";
    await performUpdate(exitAfterSave);
  };

  // Initialize once per pond-detail id.
  useEffect(() => {
    let cancelled = false;
    checkTokenValidity();
    (async () => {
      await FetchPondDetail();
      if (cancelled) return;
      await FetchFishTypesOfSamplingData({ skipLoading: true });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only remount/reload when route id changes
  }, [id]);

  useEffect(() => {
    const refreshFishSamples = () => {
      if (document.visibilityState === "visible") {
        fetchFishSamples();
      }
    };
    document.addEventListener("visibilitychange", refreshFishSamples);
    return () => document.removeEventListener("visibilitychange", refreshFishSamples);
  }, [fetchFishSamples]);

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-workflow-page donezo-upload-page">
      <form onSubmit={handleFormSubmit}>
        <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
          <PondDetailWorkflowPageHeader
            pondDetailId={id}
            section="general"
            detailName={formData.Name}
            noBottomMargin
            actions={
              <>
                <button
                  type="submit"
                  name="saveAction"
                  value="stay"
                  className="donezo-orders-toolbar__ghost"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating…" : "Update"}
                </button>
                <button
                  type="submit"
                  name="saveAction"
                  value="exit"
                  className="donezo-orders-toolbar__primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating…" : "Update and exit"}
                </button>
              </>
            }
          />

          <div className="mt-3">
            <PondDetailWorkflowTabs id={id} active="general" />
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-info-circle" aria-hidden />
              <h2 className="donezo-upload-section__title">Basic Information</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-lg-4">
                  <div className="form-floating">
                    <select
                      className={`form-control ${formErrors.PondID ? "is-invalid" : ""}`}
                      id="PondID"
                      name="PondID"
                      value={formData.PondID}
                      onChange={handlePondChange}
                    >
                      <option value="0">Select Pond</option>
                      {ponds.map((pond) => (
                        <option key={pond.id} value={pond.id}>
                          {pond.Name}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="PondID">
                      Pond <span className="text-danger">*</span>
                    </label>
                    {formErrors.PondID && (
                      <div className="invalid-feedback">{formErrors.PondID}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <div className="form-floating">
                    <input
                      type="datetime-local"
                      className={`form-control ${formErrors.Date ? "is-invalid" : ""}`}
                      id="Date"
                      value={formData.Date ? formatLocalDateTime(formData.Date) : ""}
                      onChange={(e) => handleDateChange(e, 1)}
                    />
                    <label htmlFor="Date">
                      Sampling/Fishing Date &amp; Time{" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.Date && (
                      <div className="invalid-feedback">{formErrors.Date}</div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-lg-4">
                  <FishTypesSelection
                    fishTypes={pondFishTypes}
                    selectedFishTypes={samplingFishTypes}
                    onChange={handleFishTypesChange}
                    error={formErrors.FishTypes}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-clock" aria-hidden />
              <h2 className="donezo-upload-section__title">Sampling Details</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <ClockTimeInput
                      className={`form-control ${
                        formErrors.TimeFishingStarted ? "is-invalid" : ""
                      }`}
                      id="TimeFishingStarted"
                      value={formData.TimeFishingStarted}
                      onChange={handleChange}
                      placeholder="1200 AM"
                    />
                    <label htmlFor="TimeFishingStarted">
                      Timer start (h:mm AM/PM){" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.TimeFishingStarted && (
                      <div className="invalid-feedback">
                        {formErrors.TimeFishingStarted}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <ClockTimeInput
                      className={`form-control ${
                        formErrors.TimeFishingStopped ? "is-invalid" : ""
                      }`}
                      id="TimeFishingStopped"
                      value={formData.TimeFishingStopped}
                      onChange={handleChange}
                      placeholder="1214 PM"
                    />
                    <label htmlFor="TimeFishingStopped">
                      Timer stop (h:mm AM/PM){" "}
                      <span className="text-danger">*</span>
                    </label>
                    {formErrors.TimeFishingStopped && (
                      <div className="invalid-feedback">
                        {formErrors.TimeFishingStopped}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-3">
                  <div className="form-floating">
                    <select
                      className={`form-control ${
                        formErrors.SamplingMethod ? "is-invalid" : ""
                      }`}
                      id="SamplingMethod"
                      value={formData.SamplingMethod}
                      onChange={handleChange}
                    >
                      <option value="">Select Sampling Method</option>
                      {SAMPLING_METHOD_OPTIONS.map((m) => (
                        <option key={m} value={m}>
                          {displayMethodLabel(m)}
                        </option>
                      ))}
                    </select>
                    <label htmlFor="SamplingMethod">
                      Sampling Method <span className="text-danger">*</span>
                    </label>
                    {formErrors.SamplingMethod && (
                      <div className="invalid-feedback">
                        {formErrors.SamplingMethod}
                      </div>
                    )}
                  </div>
                </div>

                <SamplingEffortFields
                  samplingMethod={formData.SamplingMethod}
                  effortCount={effortCount}
                  onEffortCountChange={setEffortCount}
                  effortUnits={effortUnits}
                  onEffortUnitsChange={setEffortUnits}
                  anglerCount={anglerCount}
                  onAnglerCountChange={handleAnglerCountChange}
                  fisherStaffOptions={fisherStaffOptions}
                  sessionAnglerSlots={sessionAnglerSlots}
                  onSessionAnglerSlotsChange={handleSessionAnglerSlotsChange}
                  sessionAnglersError={formErrors.SessionAnglers}
                  fishSamples={fishSamples}
                  pondAnglers={pondAnglers}
                />
              </div>
            </div>
          </div>

          {showWeatherPanel && (
          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i className="fa fa-cloud-sun" aria-hidden />
              <h2 className="donezo-upload-section__title">Weather Conditions</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${formErrors.AirTemperature ? "is-invalid" : ""}`}
                      id="AirTemperature"
                      value={formData.AirTemperature}
                      onChange={handleChange}
                    />
                    <label htmlFor="AirTemperature">Air Temperature (°F)</label>
                    {formErrors.AirTemperature && (
                      <div className="invalid-feedback">{formErrors.AirTemperature}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <select
                      className={`form-control ${formErrors.SkyClimate ? "is-invalid" : ""}`}
                      id="SkyClimate"
                      value={formData.SkyClimate}
                      onChange={handleChange}
                    >
                      <option value="">Select Sky</option>
                      <option value="Sunny">Sunny</option>
                      <option value="Cloudy">Cloudy</option>
                      <option value="Partly Cloudy">Partly Cloudy</option>
                      <option value="Rain">Rain</option>
                      <option value="Snow">Snow</option>
                    </select>
                    <label htmlFor="SkyClimate">Sky</label>
                    {formErrors.SkyClimate && (
                      <div className="invalid-feedback">{formErrors.SkyClimate}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${formErrors.WindSpeed ? "is-invalid" : ""}`}
                      id="WindSpeed"
                      value={formData.WindSpeed}
                      onChange={handleChange}
                    />
                    <label htmlFor="WindSpeed">Wind Speed (mph)</label>
                    {formErrors.WindSpeed && (
                      <div className="invalid-feedback">{formErrors.WindSpeed}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <select
                      className={`form-control ${formErrors.WindDirection ? "is-invalid" : ""}`}
                      id="WindDirection"
                      value={formData.WindDirection}
                      onChange={handleChange}
                    >
                      <option value="">Select Wind Direction</option>
                      <option value="North">North</option>
                      <option value="Northeast">Northeast</option>
                      <option value="East">East</option>
                      <option value="Southeast">Southeast</option>
                      <option value="South">South</option>
                      <option value="Southwest">Southwest</option>
                      <option value="West">West</option>
                      <option value="Northwest">Northwest</option>
                    </select>
                    <label htmlFor="WindDirection">Wind Direction</label>
                    {formErrors.WindDirection && (
                      <div className="invalid-feedback">{formErrors.WindDirection}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <select
                      className={`form-control ${formErrors.MoonPhase ? "is-invalid" : ""}`}
                      id="MoonPhase"
                      value={formData.MoonPhase}
                      onChange={handleChange}
                    >
                      <option value="">Select Moon Phase</option>
                      <option value="New">New</option>
                      <option value="First Qtr">First Qtr</option>
                      <option value="Full">Full</option>
                      <option value="Last Qtr">Last Qtr</option>
                    </select>
                    <label htmlFor="MoonPhase">Moon Phase</label>
                    {formErrors.MoonPhase && (
                      <div className="invalid-feedback">{formErrors.MoonPhase}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-6">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.1"
                      className={`form-control ${formErrors.BaroMetricPressure ? "is-invalid" : ""}`}
                      id="BaroMetricPressure"
                      value={formData.BaroMetricPressure}
                      onChange={handleChange}
                    />
                    <label htmlFor="BaroMetricPressure">Barometric Pressure (mmHg)</label>
                    {formErrors.BaroMetricPressure && (
                      <div className="invalid-feedback">{formErrors.BaroMetricPressure}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          <div className="donezo-orders-panel donezo-upload-section mb-3">
            <div className="donezo-upload-section__header">
              <i
                className={`fa ${showFertilizationDateType ? "fa-seedling" : "fa-balance-scale"}`}
                aria-hidden
              />
              <h2 className="donezo-upload-section__title">
                {showFertilizationDateType
                  ? "Fertilization & units"
                  : "Weight & length units"}
              </h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                {showFertilizationDateType && (
                  <>
                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="datetime-local"
                          className="form-control"
                          id="FertilizationDate"
                          value={
                            formData.FertilizationDate
                              ? formatLocalDateTime(formData.FertilizationDate)
                              : ""
                          }
                          onChange={(e) => handleDateChange(e, 2)}
                        />
                        <label htmlFor="FertilizationDate">Fertilization Date</label>
                      </div>
                    </div>
                    <div className="col-12 col-md-6">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="FertilizationType"
                          value={formData.FertilizationType}
                          onChange={handleChange}
                        />
                        <label htmlFor="FertilizationType">Fertilization Type</label>
                      </div>
                    </div>
                  </>
                )}
                <div className="col-12">
                  <PondDetailStandardWeightUnits
                    weightUnitId={selectedWeightUnit}
                    lengthUnitId={selectedLengthUnit}
                    onUnitsChange={handleStandardWeightUnitsChange}
                    systemError={formErrors.StandardWeightSystem}
                  />
                </div>
              </div>
            </div>
          </div>

          {showWaterQualityPanel && (
          <div className="donezo-orders-panel donezo-upload-section mb-0">
            <div className="donezo-upload-section__header">
              <i className="fa fa-tint" aria-hidden />
              <h2 className="donezo-upload-section__title">Water Quality</h2>
            </div>
            <div className="donezo-upload-section__body">
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${formErrors.WaterTemperature ? "is-invalid" : ""}`}
                      id="WaterTemperature"
                      value={formData.WaterTemperature}
                      onChange={handleChange}
                    />
                    <label htmlFor="WaterTemperature">Water Temperature, F</label>
                    {formErrors.WaterTemperature && (
                      <div className="invalid-feedback">{formErrors.WaterTemperature}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      id="WaterClarity"
                      value={formData.WaterClarity}
                      onChange={handleChange}
                    />
                    <label htmlFor="WaterClarity">Water Clarity, ft.</label>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${formErrors.PH ? "is-invalid" : ""}`}
                      id="PH"
                      value={formData.PH}
                      onChange={handleChange}
                    />
                    <label htmlFor="PH">pH (1-14)</label>
                    {formErrors.PH && (
                      <div className="invalid-feedback">{formErrors.PH}</div>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="form-floating">
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      id="DO"
                      value={formData.DO}
                      onChange={handleChange}
                    />
                    <label htmlFor="DO">Dissolved Oxygen, mg/L</label>
                  </div>
                </div>
                {WQ_EXTRA_FIELD_DEFS.map((def) => (
                  <div key={def.id} className="col-12 col-md-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        id={def.id}
                        value={formData[def.id]}
                        onChange={handleChange}
                      />
                      <label htmlFor={def.id}>{def.label}</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}

        </div>
      </form>
    </div>
  );
};

export default UpdatePD;